import { useState, useCallback } from 'react';
import { POWER_SKILLS, CATEGORIES } from '@/data/categories';

// Fisher-Yates shuffle
const shuffle = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// LocalStorage key for skill queues
const STORAGE_KEY = 'psw_skillQueues';

// Initialize queues from localStorage or create new shuffled queues
const initializeQueues = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Validate the structure
      const isValid = CATEGORIES.every(cat => 
        Array.isArray(parsed[cat]) && parsed[cat].length >= 0
      );
      if (isValid) {
        return parsed;
      }
    }
  } catch (e) {
    console.error('Failed to load skill queues:', e);
  }
  
  // Create fresh shuffled queues for each category
  const queues = {};
  CATEGORIES.forEach(category => {
    queues[category] = shuffle([...POWER_SKILLS[category]]);
  });
  return queues;
};

// Save queues to localStorage
const saveQueues = (queues) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(queues));
  } catch (e) {
    console.error('Failed to save skill queues:', e);
  }
};

export const useSkillRotation = () => {
  const [skillQueues, setSkillQueues] = useState(initializeQueues);

  // Get the next skill for a category (rotates through all 3 before repeating)
  const getNextSkill = useCallback((category) => {
    // Deep-clone the specific queue to avoid mutating React state
    const queue = [...(skillQueues[category] || [])];
    
    // If queue is empty, reshuffle
    if (queue.length === 0) {
      queue.push(...shuffle([...POWER_SKILLS[category]]));
    }
    
    // Pop the next skill
    const skill = queue.pop();
    
    // Update state and localStorage immutably
    const updated = { ...skillQueues, [category]: queue };
    setSkillQueues(updated);
    saveQueues(updated);
    
    return skill;
  }, [skillQueues]);

  // Reset all queues (for testing/debugging)
  const resetQueues = useCallback(() => {
    const queues = {};
    CATEGORIES.forEach(category => {
      queues[category] = shuffle([...POWER_SKILLS[category]]);
    });
    setSkillQueues(queues);
    saveQueues(queues);
  }, []);

  return {
    getNextSkill,
    resetQueues,
    skillQueues
  };
};

export default useSkillRotation;
